/**
 * Analytics Hook
 * Provides easy-to-use functions for tracking events
 */

import { useCallback } from 'react'
import { trackEvent, identifyUser as identifyPostHogUser, resetUser } from '@/lib/posthog'

export function useAnalytics() {
  // Track analysis events
  const trackAnalysis = useCallback((action: 'started' | 'completed' | 'failed', properties?: {
    projectId?: string
    defectCount?: number
    processingTime?: number
    model?: string
    error?: string
    errorType?: string
    hasDefects?: boolean
  }) => {
    trackEvent(`analysis_${action}`, properties)
  }, [])

  // Track project events
  const trackProject = useCallback((action: 'created' | 'updated' | 'archived' | 'switched', properties?: {
    projectId: string
    projectName?: string
    hasDescription?: boolean
    hasAddress?: boolean
    setAsActive?: boolean
  }) => {
    trackEvent(`project_${action}`, properties)
  }, [])

  // Track ticket events
  const trackTicket = useCallback((action: 'created' | 'sent' | 'delivered' | 'sent_queued' | 'sent_completed' | 'sent_failed', properties?: {
    ticketId: string
    contractorId?: string
    defectCount?: number
    hasProject?: boolean
    hasNotes?: boolean
    usedTemplate?: boolean
    templateId?: string
    intentId?: string
    latencyMs?: number
    error?: string
  }) => {
    trackEvent(`ticket_${action}`, properties)
  }, [])

  // Track subscription events
  const trackSubscription = useCallback((action: 'checkout_started' | 'created' | 'cancelled' | 'usage_limit_reached', properties?: {
    userId?: string
    plan?: string
    analysisCount?: number
  }) => {
    trackEvent(`subscription_${action}`, properties)
  }, [])

  // Track authentication events
  const trackAuth = useCallback((action: 'registered' | 'login' | 'logout', properties?: {
    userId?: string
    role?: string
  }) => {
    trackEvent(`user_${action}`, properties)
  }, [])

  // Identify user after login
  const identifyUser = useCallback((userId: string, traits?: {
    role?: string
    company?: string
  }) => {
    identifyPostHogUser(userId, traits)
  }, [])

  // Reset on logout
  const resetAnalytics = useCallback(() => {
    resetUser()
  }, [])

  return {
    // Analysis tracking
    trackAnalysis,

    // Project tracking
    trackProject,

    // Ticket tracking
    trackTicket,

    // Subscription tracking
    trackSubscription,

    // Authentication tracking
    trackAuth,

    // User identification
    identifyUser,
    resetAnalytics,

    // Generic event tracking
    trackEvent,
  }
}
